import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Space,
  Tag,
  message,
  Popconfirm,
  Tooltip,
  Row,
  Col,
  Alert,
  InputNumber,
  Descriptions,
  Badge,
  Collapse,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SendOutlined,
  SyncOutlined,
  EyeOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import {
  whatsappConnectorService,
  WhatsAppPartner,
  CreateWhatsAppPartnerRequest,
  UpdateWhatsAppPartnerRequest,
  TestConnectionResult
} from '../services/whatsappConnectorService';

const { Option } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;

const WhatsAppPartnersPage: React.FC = () => {
  // 状态管理
  const [loading, setLoading] = useState(false);
  const [partners, setPartners] = useState<WhatsAppPartner[]>([]);
  const [editingPartner, setEditingPartner] = useState<WhatsAppPartner | null>(null);
  const [partnerModalVisible, setPartnerModalVisible] = useState(false);
  const [testingPartner, setTestingPartner] = useState<number | null>(null);
  const [testResults, setTestResults] = useState<{ [key: number]: TestConnectionResult }>({});
  const [configTemplates, setConfigTemplates] = useState<any>({});
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<any>(null);

  // 表单
  const [partnerForm] = Form.useForm();

  // 加载数据
  const loadPartners = async () => {
    try {
      setLoading(true);
      const response = await whatsappConnectorService.getWhatsAppPartners();
      setPartners(response.data || []);
    } catch (error: any) {
      message.error('加载Partner列表失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 加载配置模板
  const loadConfigTemplates = async () => {
    try {
      const response = await whatsappConnectorService.getPartnerConfigTemplates();
      setConfigTemplates(response.data || {});
    } catch (error: any) {
      console.error('加载配置模板失败:', error);
    }
  };

  // 处理Partner类型变化
  const handlePartnerTypeChange = (partnerType: string) => {
    const template = configTemplates[partnerType];
    if (template) {
      // 更新API端点
      partnerForm.setFieldsValue({
        apiEndpoint: template.api_endpoint || '',
        config: JSON.stringify(template.config || {}, null, 2)
      });
    }
  };

  // 查看配置详情
  const handleViewConfig = (config: any) => {
    try {
      const configObj = typeof config === 'string' ? JSON.parse(config) : config;
      setSelectedConfig(configObj);
      setConfigModalVisible(true);
    } catch (error) {
      message.error('配置格式错误，无法显示');
    }
  };

  useEffect(() => {
    loadPartners();
    loadConfigTemplates();
  }, []);

  // 测试连接
  const testConnection = async (partnerId: number) => {
    try {
      setTestingPartner(partnerId);
      const result = await whatsappConnectorService.testWhatsAppPartnerConnection(partnerId);
      setTestResults(prev => ({ ...prev, [partnerId]: result }));
      
      if (result.success) {
        message.success('连接测试成功');
      } else {
        message.error('连接测试失败: ' + result.message);
      }
    } catch (error: any) {
      message.error('连接测试失败: ' + error.message);
      setTestResults(prev => ({ 
        ...prev, 
        [partnerId]: { success: false, message: error.message } 
      }));
    } finally {
      setTestingPartner(null);
    }
  };

  // 保存Partner
  const handleSavePartner = async (values: any) => {
    try {
      setLoading(true);
      
      if (editingPartner) {
        const updateData: UpdateWhatsAppPartnerRequest = {
          ...values
        };
        await whatsappConnectorService.updateWhatsAppPartner(editingPartner.id, updateData);
        message.success('Partner更新成功');
      } else {
        const createData: CreateWhatsAppPartnerRequest = {
          ...values,
          tenantCode: localStorage.getItem('currentTenant') || 'default'
        };
        await whatsappConnectorService.createWhatsAppPartner(createData);
        message.success('Partner创建成功');
      }

      setPartnerModalVisible(false);
      setEditingPartner(null);
      partnerForm.resetFields();
      await loadPartners();
    } catch (error: any) {
      message.error('保存Partner失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 删除Partner
  const handleDeletePartner = async (id: number) => {
    try {
      setLoading(true);
      await whatsappConnectorService.deleteWhatsAppPartner(id);
      message.success('Partner删除成功');
      await loadPartners();
    } catch (error: any) {
      message.error('删除Partner失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 编辑Partner
  const handleEditPartner = (partner: WhatsAppPartner) => {
    setEditingPartner(partner);
    partnerForm.setFieldsValue({
      ...partner,
      config: partner.config ? JSON.stringify(partner.config, null, 2) : ''
    });
    setPartnerModalVisible(true);
  };

  // 获取Partner类型标签
  const getPartnerTypeTag = (type: string) => {
    const typeMap: { [key: string]: { color: string; text: string } } = {
      'meta_official': { color: 'blue', text: 'Meta官方' },
      '360dialog': { color: 'green', text: '360Dialog' },
      'twilio': { color: 'red', text: 'Twilio' },
      'messagebird': { color: 'orange', text: 'MessageBird' },
      'custom': { color: 'purple', text: '自定义' }
    };
    
    const config = typeMap[type] || { color: 'default', text: type };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 获取状态标签
  const getStatusTag = (isActive: boolean) => {
    return isActive ? (
      <Tag color="green" icon={<CheckCircleOutlined />}>激活</Tag>
    ) : (
      <Tag color="red" icon={<ExclamationCircleOutlined />}>未激活</Tag>
    );
  };

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Partner名称',
      dataIndex: 'partnerName',
      key: 'partnerName',
      width: 150,
    },
    {
      title: '类型',
      dataIndex: 'partnerType',
      key: 'partnerType',
      width: 120,
      render: (type: string) => getPartnerTypeTag(type),
    },
    {
      title: '代码',
      dataIndex: 'partnerCode',
      key: 'partnerCode',
      width: 120,
    },
    {
      title: 'API端点',
      dataIndex: 'apiEndpoint',
      key: 'apiEndpoint',
      width: 200,
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean) => getStatusTag(isActive),
    },
    {
      title: '配置参数',
      dataIndex: 'config',
      key: 'config',
      width: 200,
      render: (config: any) => {
        if (!config) return <Tag color="default">无配置</Tag>;
        
        try {
          const configObj = typeof config === 'string' ? JSON.parse(config) : config;
          const keyCount = Object.keys(configObj).length;
          return (
            <Tooltip title="点击查看详细配置">
              <Tag 
                color="blue" 
                style={{ cursor: 'pointer' }}
                onClick={() => handleViewConfig(config)}
              >
                {keyCount} 个配置项
              </Tag>
            </Tooltip>
          );
        } catch (error) {
          return <Tag color="red">配置格式错误</Tag>;
        }
      },
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      render: (record: WhatsAppPartner) => (
        <Space>
          <Tooltip title="测试连接">
            <Button
              type="link"
              icon={<SendOutlined />}
              loading={testingPartner === record.id}
              onClick={() => testConnection(record.id)}
            >
              测试
            </Button>
          </Tooltip>
          <Tooltip title="编辑">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditPartner(record)}
            >
              编辑
            </Button>
          </Tooltip>
          <Popconfirm
            title="确定要删除这个Partner吗？"
            onConfirm={() => handleDeletePartner(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
              >
                删除
              </Button>
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingPartner(null);
            partnerForm.resetFields();
            setPartnerModalVisible(true);
          }}
        >
          新建Partner
        </Button>
        <Button
          icon={<SyncOutlined />}
          onClick={loadPartners}
          style={{ marginLeft: 8 }}
        >
          刷新
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={partners}
        rowKey="id"
        loading={loading}
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
        }}
        expandable={{
          expandedRowRender: (record) => (
            <div style={{ margin: 0 }}>
              <Descriptions size="small" column={2}>
                <Descriptions.Item label="创建时间">
                  {record.createdAt ? new Date(record.createdAt).toLocaleString() : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="更新时间">
                  {record.updatedAt ? new Date(record.updatedAt).toLocaleString() : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="API Key" span={2}>
                  {record.apiKey ? '***' + record.apiKey.slice(-8) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Access Token" span={2}>
                  {record.accessToken ? '***' + record.accessToken.slice(-8) : '-'}
                </Descriptions.Item>
              </Descriptions>
              
              {record.config && (
                <>
                  <Divider />
                  <Collapse size="small">
                    <Panel header="配置详情" key="config">
                      <pre style={{ 
                        background: '#f5f5f5', 
                        padding: '8px', 
                        borderRadius: '4px',
                        fontSize: '12px',
                        maxHeight: '200px',
                        overflow: 'auto'
                      }}>
                        {JSON.stringify(record.config, null, 2)}
                      </pre>
                    </Panel>
                  </Collapse>
                </>
              )}

              {testResults[record.id] && (
                <>
                  <Divider />
                  <Alert
                    message="连接测试结果"
                    type={testResults[record.id].success ? 'success' : 'error'}
                    description={
                      <div>
                        <p>{testResults[record.id].message}</p>
                        {testResults[record.id].response_time && (
                          <p>响应时间: {testResults[record.id].response_time}ms</p>
                        )}
                      </div>
                    }
                    showIcon
                  />
                </>
              )}
            </div>
          ),
          rowExpandable: (record) => true,
        }}
      />

      {/* Partner编辑模态框 */}
      <Modal
        title={editingPartner ? '编辑Partner' : '新建Partner'}
        open={partnerModalVisible}
        onCancel={() => {
          setPartnerModalVisible(false);
          setEditingPartner(null);
          partnerForm.resetFields();
        }}
        onOk={() => partnerForm.submit()}
        width={800}
        confirmLoading={loading}
      >
        <Form
          form={partnerForm}
          layout="vertical"
          onFinish={handleSavePartner}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="partnerName"
                label="Partner名称"
                rules={[{ required: true, message: '请输入Partner名称' }]}
              >
                <Input placeholder="请输入Partner名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="partnerType"
                label="Partner类型"
                rules={[{ required: true, message: '请选择Partner类型' }]}
              >
                <Select 
                  placeholder="请选择Partner类型"
                  onChange={handlePartnerTypeChange}
                >
                  <Option value="meta_official">Meta官方</Option>
                  <Option value="360dialog">360Dialog</Option>
                  <Option value="twilio">Twilio</Option>
                  <Option value="messagebird">MessageBird</Option>
                  <Option value="custom">自定义</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="partnerCode"
                label="Partner代码"
                rules={[{ required: true, message: '请输入Partner代码' }]}
              >
                <Input placeholder="请输入Partner代码" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="isActive"
                label="状态"
                valuePropName="checked"
              >
                <Switch checkedChildren="激活" unCheckedChildren="未激活" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="apiEndpoint"
            label="API端点"
          >
            <Input placeholder="请输入API端点" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="apiKey"
                label="API Key"
              >
                <Input.Password placeholder="请输入API Key" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="accessToken"
                label="Access Token"
              >
                <Input.Password placeholder="请输入Access Token" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="描述"
          >
            <TextArea rows={3} placeholder="请输入描述" />
          </Form.Item>

          <Form.Item
            name="config"
            label="配置参数 (JSON格式)"
            extra="选择Partner类型后会自动填充默认配置，您可以根据需要修改"
          >
            <TextArea 
              rows={8} 
              placeholder='选择Partner类型后会自动填充默认配置...'
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 配置详情模态框 */}
      <Modal
        title="配置参数详情"
        open={configModalVisible}
        onCancel={() => {
          setConfigModalVisible(false);
          setSelectedConfig(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setConfigModalVisible(false);
            setSelectedConfig(null);
          }}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {selectedConfig && (
          <div>
            <Descriptions bordered column={1} size="small">
              {Object.entries(selectedConfig).map(([key, value]) => (
                <Descriptions.Item 
                  key={key} 
                  label={
                    <span style={{ fontWeight: 'bold' }}>
                      {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  }
                >
                  {typeof value === 'object' ? (
                    <pre style={{ 
                      margin: 0, 
                      whiteSpace: 'pre-wrap',
                      fontSize: '12px',
                      fontFamily: 'monospace',
                      background: '#f5f5f5',
                      padding: '8px',
                      borderRadius: '4px'
                    }}>
                      {JSON.stringify(value, null, 2)}
                    </pre>
                  ) : (
                    <span style={{ 
                      fontFamily: 'monospace',
                      background: '#f5f5f5',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      fontSize: '12px'
                    }}>
                      {String(value)}
                    </span>
                  )}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default WhatsAppPartnersPage;
